const PUSH_DEBOUNCE_MS = 1500

export type SyncStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

/** Runs a callback after a delay; returns a cancel function. */
export type SyncScheduler = (callback: () => void, delayMs: number) => () => void

/**
 * Returns a scheduler backed by `setTimeout`.
 *
 * @param callback - The function to run.
 * @param delayMs - The delay.
 * @returns A cancel function.
 */
function defaultSchedule(callback: () => void, delayMs: number) {
   const timerId = setTimeout(callback, delayMs)
   return () => clearTimeout(timerId)
}

/**
 * Resolves the storage a delta sync keeps its bookkeeping in.
 *
 * @param storage - An injected storage, if any.
 * @returns The storage, localStorage, or null when neither exists.
 */
export function resolveSyncStorage(storage?: SyncStorage) {
   return storage ?? (typeof localStorage !== 'undefined' ? localStorage : null)
}

/**
 * Reads a stored JSON object.
 *
 * @param storage - The storage, or null.
 * @param key - The storage key.
 * @returns The object, or null when missing, unparseable, or not an object.
 */
export function readStoredObject(storage: SyncStorage | null, key: string) {
   try {
      const raw = storage?.getItem(key)
      const value: unknown = raw ? JSON.parse(raw) : null
      return value && typeof value === 'object' ?
            (value as Record<string, unknown>)
         :  null
   } catch {
      return null
   }
}

/**
 * Stores a JSON object, or removes the key when null.
 *
 * @param storage - The storage, or null.
 * @param key - The storage key.
 * @param value - The object to store, or null to clear it.
 */
export function writeStoredObject(
   storage: SyncStorage | null,
   key: string,
   value: object | null,
) {
   try {
      if (value) {
         storage?.setItem(key, JSON.stringify(value))
      } else {
         storage?.removeItem(key)
      }
   } catch {
      // Storage unavailable: the next session bootstraps again, which is safe.
   }
}

type DeltaSyncRunnerOptions = {
   /** True until the first full pull has happened. */
   needsBootstrap: () => boolean
   /** Pulls server changes; resolves false when remote sync is unavailable. */
   pull: () => Promise<boolean>
   push: () => Promise<void>
   /** Collection name for error logs, e.g. `weights`. */
   label: string
   schedule?: SyncScheduler
}

/**
 * Runs push/pull cycles for a delta-synced collection: a bootstrap pull first
 * when never synced, then push and pull; concurrent requests coalesce into one
 * rerun, and local edits schedule a debounced cycle.
 *
 * @param options - The cycle steps and an optional scheduler.
 * @returns `sync`, `schedulePush`, and `cancel` (drops a scheduled push).
 */
export function createDeltaSyncRunner(options: DeltaSyncRunnerOptions) {
   const schedule = options.schedule ?? defaultSchedule
   let cancelScheduledPush: (() => void) | null = null
   let inFlight: Promise<void> | null = null
   let rerunRequested = false

   /**
    * Runs a sync cycle, coalescing concurrent requests into one rerun.
    *
    * @returns A promise that settles when the cycle (and any rerun) completes.
    */
   function sync(): Promise<void> {
      if (inFlight) {
         rerunRequested = true
         return inFlight
      }
      inFlight = (async () => {
         try {
            do {
               rerunRequested = false
               if (options.needsBootstrap() && !(await options.pull())) {
                  return
               }
               await options.push()
               await options.pull()
            } while (rerunRequested)
         } finally {
            inFlight = null
         }
      })()
      return inFlight
   }

   /**
    * Drops a scheduled push.
    */
   function cancel() {
      cancelScheduledPush?.()
      cancelScheduledPush = null
   }

   /**
    * Schedules a debounced sync after a local edit.
    */
   function schedulePush() {
      cancel()
      cancelScheduledPush = schedule(() => {
         cancelScheduledPush = null
         sync().catch(error => {
            console.error(`Failed to push ${options.label}`, error)
         })
      }, PUSH_DEBOUNCE_MS)
   }

   return { sync, schedulePush, cancel }
}
